import { Modal, Button, Table, NumberInput, Group, Stack, Text } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useState, useEffect, useCallback } from "react";
import moment from "moment";
import notify from "../utils/Notification";
import { createB2BAPI } from "../api/Interceptor";
import "./DeliveryScheduleForm.css";
import dayjs from "dayjs";

const DeliveryScheduleForm = ({ opened, onClose, purchaseOrder, setPurchaseOrder, orderType, status }) => {
    const B2B_API = createB2BAPI();
    const [schedule, setSchedule] = useState([]);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!purchaseOrder) return;
        const mainDeliveryDate = purchaseOrder.deliveryDate ? new Date(purchaseOrder.deliveryDate) : null;

        const initialSchedule = (purchaseOrder.items || []).map((item, index) => {
            const baseDate = moment().startOf("day");
            const computeDate = (key) => {
                const leadTime = item[`${key}LeadTime`] || 0;
                return {
                    delivery: baseDate.clone().add(leadTime, "days").toDate(),
                    dispatch: baseDate.clone().add(leadTime, "days").subtract(1, "day").toDate(),
                };
            };

            const fabricDates = computeDate("fabric");
            const dyeLotDates = computeDate("dyeLot");
            const greigeDates = computeDate("greige");

            return {
                id: item.id || `item-${index}`,
                productName: item.productName || item.productVariant?.name || "",
                costPrice: item.costPrice || 0,
                orderedRolls: item.orderedRolls || 0,
                stdKg: item.stdKg || purchaseOrder?.stdKg || 20,
                variantSku: item.variantSku || purchaseOrder?.variantSku || "",
                swatchId: item.swatchId || purchaseOrder?.swatchId || "",
                qty: item.qty || 0,
                fabricQty: item.fabricQty || 0,
                noOfFabricRolls: item.noOfFabricRolls || 0,
                dyeLotQty: item.dyeLotQty || 0,
                noOfDyeLotRolls: item.noOfDyeLotRolls || 0,
                greigeQty: item.greigeQty || 0,
                noOfGreigeRolls: item.noOfGreigeRolls || 0,
                dyeLotDeliveryDate: item.dyeLotDeliveryDate ? new Date(item.dyeLotDeliveryDate) : dyeLotDates.delivery,
                dyeLotDispatchDate: item.dyeLotDispatchDate ? new Date(item.dyeLotDispatchDate) : dyeLotDates.dispatch,
                fabricDeliveryDate: item.fabricDeliveryDate ? new Date(item.fabricDeliveryDate) : fabricDates.delivery,
                fabricDispatchDate: item.fabricDispatchDate ? new Date(item.fabricDispatchDate) : fabricDates.dispatch,
                greigeDeliveryDate: item.greigeDeliveryDate ? new Date(item.greigeDeliveryDate) : greigeDates.delivery,
                greigeDispatchDate: item.greigeDispatchDate ? new Date(item.greigeDispatchDate) : greigeDates.dispatch,
                deliveryDate: (orderType === "SWATCH" || orderType === "SAMPLE") ? mainDeliveryDate : (item.deliveryDate ? new Date(item.deliveryDate) : null),
                // deliveryDate:
                //     orderType === "SWATCH" || orderType === "SAMPLE"
                //         ? purchaseOrder.deliveryDate
                //             ? new Date(purchaseOrder.deliveryDate)
                //             : null
                //         : item.deliveryDate
                //             ? new Date(item.deliveryDate)
                //             : null,
                originalItem: item,
            };
        });

        setSchedule(initialSchedule);
    }, [purchaseOrder, orderType]);

    const isEditable = useCallback((fieldKey, itemIndex) => {
        if (status === 'third') return false;
        const item = schedule[itemIndex];
        const totalRolls = item.orderedRolls || 0;
        const rollsFabric = item.noOfFabricRolls || 0;
        const rollsGreige = item.noOfGreigeRolls || 0;
        const rollsDyeLot = item.noOfDyeLotRolls || 0;


        if (orderType === 'WHOLESALE') {
            if (['dyeLotDeliveryDate', 'dyeLotQty', 'noOfDyeLotRolls'].includes(fieldKey) && (rollsFabric + rollsGreige) >= totalRolls) return false;
            if (['greigeDeliveryDate', 'greigeQty', 'noOfGreigeRolls'].includes(fieldKey) && (rollsFabric + rollsDyeLot) >= totalRolls) return false;
            if (['fabricDeliveryDate', 'fabricQty', 'noOfFabricRolls'].includes(fieldKey) && (rollsGreige + rollsDyeLot) >= totalRolls) return false;
        }

        return true;
    }, [schedule, status, orderType]);
    const allRollsAllocated = schedule.every(item => {
        if (orderType !== "WHOLESALE") return true;
        const total = item.orderedRolls || 0;
        const allocated = (item.noOfFabricRolls || 0) + (item.noOfDyeLotRolls || 0) + (item.noOfGreigeRolls || 0);
        return allocated === total;
    });

    const getRemaining = useCallback((itemIndex, field) => {
        const item = schedule[itemIndex];
        const totalRolls = item.orderedRolls || 0;
        const rollsFabric = item.noOfFabricRolls || 0;
        const rollsGreige = item.noOfGreigeRolls || 0;
        const rollsDyeLot = item.noOfDyeLotRolls || 0;

        let usedRolls = rollsFabric + rollsGreige + rollsDyeLot;
        if (field === 'noOfFabricRolls') usedRolls -= rollsFabric;
        if (field === 'noOfGreigeRolls') usedRolls -= rollsGreige;
        if (field === 'noOfDyeLotRolls') usedRolls -= rollsDyeLot;

        return Math.max(0, totalRolls - usedRolls);
    }, [schedule]);

    const handleInputChange = useCallback((itemId, field, value) => {
        setSchedule(prev => prev.map(i => {
            if (i.id !== itemId) return i;
            const updated = { ...i, [field]: value };
            const stdKg = updated.stdKg || 20;

            if (field === 'noOfFabricRolls') updated.fabricQty = updated.noOfFabricRolls * stdKg;
            if (field === 'noOfGreigeRolls') updated.greigeQty = updated.noOfGreigeRolls * stdKg;
            if (field === 'noOfDyeLotRolls') updated.dyeLotQty = updated.noOfDyeLotRolls * stdKg;

            if (field === 'fabricQty') updated.noOfFabricRolls = Math.floor(updated.fabricQty / stdKg);
            if (field === 'greigeQty') updated.noOfGreigeRolls = Math.floor(updated.greigeQty / stdKg);
            if (field === 'dyeLotQty') updated.noOfDyeLotRolls = Math.floor(updated.dyeLotQty / stdKg);

            return updated;
        }));
    }, []);

    const formatDate = (date) => (date ? moment(date).format("YYYY-MM-DD") : null);

    const validateSchedule = useCallback(() => {
        const newErrors = {};
        schedule.forEach((item, idx) => {
            const totalRolls = item.orderedRolls || 0;
            const sumRolls = (item.noOfFabricRolls || 0) + (item.noOfGreigeRolls || 0) + (item.noOfDyeLotRolls || 0);

            if (sumRolls > totalRolls) {
                newErrors[`rolls_${item.id}`] = `Total rolls (${sumRolls}) cannot exceed ordered rolls (${totalRolls})`;
            }
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [schedule]);

    const handleSaveSchedule = async () => {
        if (!validateSchedule()) {
            notify({ error: true, message: 'Please correct the errors before saving.' });
            return;
        }

        try {
            setLoading(true);
            const updatedPurchaseOrder = {
                ...purchaseOrder,
                stockSchedules: schedule.map(item => ({
                    ...item,
                    fabricDispatchDate: formatDate(item.fabricDispatchDate),
                    dyeLotDispatchDate: formatDate(item.dyeLotDispatchDate),
                    greigeDispatchDate: formatDate(item.greigeDispatchDate),
                    fabricDeliveryDate: formatDate(item.fabricDeliveryDate),
                    dyeLotDeliveryDate: formatDate(item.dyeLotDeliveryDate),
                    greigeDeliveryDate: formatDate(item.greigeDeliveryDate),
                    deliveryDate: formatDate(item.deliveryDate),
                })),
            };

            await B2B_API.post("purchase-order/updateOrder", { json: updatedPurchaseOrder });
            setPurchaseOrder(updatedPurchaseOrder);
            notify({ success: true, message: "Delivery schedule saved successfully" });
            onClose();
        } catch (err) {
            console.error(err);
            notify({ error: true, message: err.message || "Error saving schedule" });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal opened={opened} onClose={onClose} title="Delivery Schedule" size="100%" centered>
            <Stack spacing="lg">
                {schedule.length === 0 && <Text>No line items found in this purchase order.</Text>}

                {schedule.map((item, idx) => (
                    <div key={item.id} className="line-item-block">
                        <Table
                            withBorder
                            withColumnBorders
                            className="delivery-schedule-table"
                            style={{ marginBottom: 5, width: "100%" }}
                        >
                            <Table.Tbody>
                                <Table.Tr>
                                    <Table.Td><b>Line Item</b></Table.Td>
                                    <Table.Td>{item?.productName}</Table.Td>
                                    <Table.Td><b>Date</b></Table.Td>
                                    <Table.Td>{moment(item.createdDate).format("DD/MM/YYYY")}</Table.Td>
                                </Table.Tr>
                                <Table.Tr>
                                    <Table.Td><b>PO Qty</b></Table.Td>
                                    <Table.Td>{item.qty}</Table.Td>

                                    {orderType === "WHOLESALE" ? (
                                        <>
                                            <Table.Td><b>PO Rolls</b></Table.Td>
                                            <Table.Td>{item.orderedRolls}</Table.Td>
                                        </>
                                    ) : (
                                        <>
                                            <Table.Td><b>Cost Price</b></Table.Td>
                                            <Table.Td>
                                                <NumberInput
                                                    value={item.costPrice}
                                                    min={0}
                                                    precision={2}
                                                    onChange={(val) => handleInputChange(item.id, "costPrice", val)}
                                                    style={{ width: "100%" }}
                                                />
                                            </Table.Td>
                                        </>
                                    )}
                                </Table.Tr>
                                <Table.Tr>
                                    {orderType === "WHOLESALE" ? (
                                        <>
                                            <Table.Td><b>Qty/Roll</b></Table.Td>
                                            <Table.Td>{item?.stdKg}</Table.Td>
                                            <Table.Td><b>Deliverable Rolls</b></Table.Td>
                                            <Table.Td>{(item?.noOfDyeLotRolls + item?.noOfFabricRolls + item?.noOfGreigeRolls)}</Table.Td>

                                        </>
                                    ) : (
                                        <>
                                            <Table.Td><b>Delivery Date</b></Table.Td>
                                            <Table.Td>
                                                <DateInput
                                                    value={item.deliveryDate ? new Date(item.deliveryDate) : null}
                                                    onChange={(val) => handleInputChange(item.id, "deliveryDate", val)}
                                                    valueFormat="DD/MM/YYYY"
                                                    minDate={new Date()}
                                                    style={{ width: "100%" }}
                                                />
                                            </Table.Td>
                                            <Table.Td><b>Dispatch Date</b></Table.Td>
                                            <Table.Td>
                                                <DateInput
                                                    value={item.dispatchDate ? new Date(item.dispatchDate) : null}
                                                    onChange={(val) => handleInputChange(item.id, "dispatchDate", val)}
                                                    valueFormat="DD/MM/YYYY"
                                                    minDate={new Date()}
                                                    maxDate={
                                                        item.deliveryDate
                                                            ? dayjs(item.deliveryDate).subtract(1, "day").toDate()
                                                            : null
                                                    }
                                                />
                                            </Table.Td>
                                        </>
                                    )}
                                </Table.Tr>
                                <Table.Tr>
                                    {orderType === "WHOLESALE" && (
                                        <>
                                            <Table.Td><b>Cost Price</b></Table.Td>
                                            <Table.Td>
                                                <NumberInput
                                                    value={item.costPrice}
                                                    min={0}
                                                    precision={2}
                                                    onChange={(val) => handleInputChange(item.id, "costPrice", val)}
                                                    style={{ width: "100%" }}
                                                    disabled={true}
                                                />
                                            </Table.Td>
                                            <Table.Td><b>Pending Rolls</b></Table.Td>
                                            <Table.Td>{(item?.orderedRolls) - (item?.noOfDyeLotRolls + item?.noOfFabricRolls + item?.noOfGreigeRolls)}</Table.Td>
                                        </>)}
                                </Table.Tr>
                            </Table.Tbody>
                        </Table>

                        {orderType === "WHOLESALE" && <Table className="delivery-schedule-table" withBorder highlightOnHover style={{ marginBottom: '20px' }}>
                            <thead>
                                <tr>
                                    <th>Sl. No.</th>
                                    <th>Production Status</th>
                                    {orderType === "WHOLESALE" && <th>Rolls</th>}
                                    <th>Quantity</th>
                                    {orderType === "WHOLESALE" && <th>Lead Time</th>}
                                    <th>Delivery Date</th>
                                    <th>Dispatch Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[
                                    { key: "fabric", label: "Finished Fabric" },
                                    { key: "dyeLot", label: "Dye Lot" },
                                    { key: "greige", label: "Greige" },
                                ].map((row, rIdx) => {
                                    const qtyKey = `${row.key}Qty`;
                                    const rollsKey = `noOf${row.key.charAt(0).toUpperCase() + row.key.slice(1)}Rolls`;
                                    const deliveryKey = `${row.key}DeliveryDate`;
                                    const dispatchKey = `${row.key}DispatchDate`;
                                    const hasRolls = orderType === "WHOLESALE" ? item[rollsKey] > 0 : true;


                                    return (
                                        <tr key={rIdx}>
                                            <td data-label="Sl. No.">{rIdx + 1}</td>
                                            <td data-label="Production Status">{row.label}</td>
                                            {orderType === "WHOLESALE" && <td data-label="Rolls">
                                                <NumberInput
                                                    value={item[rollsKey]}
                                                    disabled={!isEditable(rollsKey, idx)}
                                                    onChange={val => handleInputChange(item.id, rollsKey, val)}
                                                    min={0}
                                                    max={getRemaining(idx, rollsKey)}
                                                />
                                            </td>}
                                            {orderType === "WHOLESALE" ? <td data-label="Quantity">
                                                <div style={{ display: 'flex', flexDirection: 'row', width: '100%', alignItems: 'center' }}>
                                                    <input
                                                        type="text"
                                                        value={item[qtyKey]}
                                                        readOnly
                                                        style={{
                                                            width: '60%',
                                                            border: '1px solid #ccc',
                                                            padding: '6px 8px',
                                                            borderRadius: '4px',
                                                            textAlign: 'left',
                                                            background: '#f9f9f9',
                                                        }}
                                                    />
                                                    <small style={{ marginTop: '2px', color: '#555', fontSize: '12px', marginLeft: '8px' }}>
                                                        {item[rollsKey]} Roll x {item.stdKg || 20} Kgs
                                                    </small>
                                                </div>
                                            </td> : <td data-label="Quantity">
                                                <div style={{ display: 'flex', flexDirection: 'row', width: '100%', alignItems: 'center' }}>
                                                    <input
                                                        type="text"
                                                        value={item[qtyKey]}
                                                        readOnly
                                                        style={{
                                                            width: '60%',
                                                            border: '1px solid #ccc',
                                                            padding: '6px 8px',
                                                            borderRadius: '4px',
                                                            textAlign: 'left',
                                                            background: '#f9f9f9',
                                                        }}
                                                    />
                                                </div>
                                            </td>}
                                            {orderType === "WHOLESALE" && <td data-label="Lead Time">
                                                <input
                                                    type="text"
                                                    value={
                                                        item.originalItem
                                                            ? item.originalItem[`${row.key}LeadTime`] ?? '-'
                                                            : '-'
                                                    }
                                                    readOnly
                                                    style={{
                                                        width: '100%',
                                                        border: '1px solid #ccc',
                                                        padding: '6px 8px',
                                                        borderRadius: '4px',
                                                        textAlign: 'left',
                                                        background: '#f9f9f9',
                                                    }}
                                                />
                                            </td>}

                                            <td data-label="Delivery Date">
                                                <DateInput
                                                    value={item[deliveryKey] || (
                                                        moment().startOf("day")
                                                            .add(item.originalItem?.[`${row.key}LeadTime`] || 0, "days")
                                                            .toDate()
                                                    )}
                                                    onChange={val => handleInputChange(item.id, deliveryKey, val)}
                                                    valueFormat="DD/MM/YYYY"
                                                    minDate={new Date()}
                                                    style={{ width: "100%" }}
                                                />
                                            </td>
                                            <td data-label="Dispatch Date">
                                                <DateInput
                                                    value={item[dispatchKey] || (
                                                        moment().startOf("day")
                                                            .add(item.originalItem?.[`${row.key}LeadTime`] || 0, "days")
                                                            .subtract(1, "day")
                                                            .toDate()
                                                    )}
                                                    onChange={val => handleInputChange(item.id, dispatchKey, val)}
                                                    valueFormat="DD/MM/YYYY"
                                                    minDate={new Date()}
                                                    maxDate={
                                                        item[deliveryKey] || item.deliveryDate
                                                            ? dayjs(item[deliveryKey] || item.deliveryDate).subtract(1, "day").toDate()
                                                            : null
                                                    }
                                                    style={{ width: "100%" }}
                                                />
                                            </td>

                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>}
                        {errors[`rolls_${item.id}`] && <Text color="red">{errors[`rolls_${item.id}`]}</Text>}
                        {errors[`costPrice_${item.id}`] && <Text color="red">{errors[`costPrice_${item.id}`]}</Text>}
                    </div>
                ))}

                <Group position="right" mt="md">
                    <Button color="red" onClick={onClose}>Cancel</Button>
                    <Button loading={loading} color="#8BC53F" disabled={!allRollsAllocated} onClick={handleSaveSchedule}>Save Schedule</Button>
                </Group>
            </Stack>
        </Modal>
    );
};

export default DeliveryScheduleForm;
